import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Navbar } from "../components/Navbar";
import { Footer } from "../components/Footer";
import { Seo, faqLd, breadcrumbLd, webAppLd } from "../components/Seo";
import { COUNTRIES } from "../lib/countries";
import { calculateBudget } from "../lib/api";
import { track } from "../lib/analytics";
import {
  CurrencyInr,
  Airplane,
  Buildings,
  ForkKnife,
  Sparkle,
  Car,
  IdentificationCard,
  Calculator,
  ArrowRight,
  Lightning,
} from "@phosphor-icons/react";
import { toast } from "sonner";

const INDIAN_STATES = [
  "Andhra Pradesh", "Arunachal Pradesh", "Assam", "Bihar", "Chhattisgarh",
  "Delhi", "Goa", "Gujarat", "Haryana", "Himachal Pradesh", "Jammu & Kashmir",
  "Jharkhand", "Karnataka", "Kerala", "Madhya Pradesh", "Maharashtra", "Manipur",
  "Meghalaya", "Mizoram", "Nagaland", "Odisha", "Punjab", "Rajasthan", "Sikkim",
  "Tamil Nadu", "Telangana", "Tripura", "Uttar Pradesh", "Uttarakhand",
  "West Bengal", "Andaman & Nicobar", "Chandigarh", "Puducherry",
];

const STYLES = [
  { key: "budget", label: "Budget", hint: "Hostels · street food · public transit", color: "#3DB68B" },
  { key: "balanced", label: "Balanced", hint: "3-4★ hotels · café meals · occasional taxi", color: "#2A7FB8" },
  { key: "luxury", label: "Luxury", hint: "5★ resorts · fine dining · private transfers", color: "#FF7E4D" },
];

const BREAKDOWN_META = [
  { key: "flights", label: "Flights", Icon: Airplane, color: "#2A7FB8" },
  { key: "stays", label: "Stays", Icon: Buildings, color: "#7C6CE0" },
  { key: "food", label: "Food", Icon: ForkKnife, color: "#3DB68B" },
  { key: "activities", label: "Activities", Icon: Sparkle, color: "#F4B942" },
  { key: "local_transport", label: "Local transport", Icon: Car, color: "#FF7E4D" },
  { key: "visa_misc", label: "Visa & misc", Icon: IdentificationCard, color: "#94A3B8" },
];

const fmt = (n) => "₹" + Number(n || 0).toLocaleString("en-IN");

export default function Budget() {
  const navigate = useNavigate();
  const [state, setState] = useState("Delhi");
  const [slug, setSlug] = useState("thailand");
  const [days, setDays] = useState(7);
  const [travelers, setTravelers] = useState(2);
  const [style, setStyle] = useState("balanced");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const run = async () => {
    if (!state || !slug || !days || !travelers) {
      toast.error("Fill all fields");
      return;
    }
    track("budget_calc_started", { state, country: slug, days, travelers, style });
    setLoading(true);
    setResult(null);
    try {
      const data = await calculateBudget({
        state,
        country_slug: slug,
        days: Number(days),
        travelers: Number(travelers),
        style,
      });
      setResult(data);
      track("budget_calc_completed", {
        country: slug,
        style,
        per_person_mid: data?.total_per_person?.mid,
      });
      setTimeout(
        () => document.getElementById("budget-result")?.scrollIntoView({ behavior: "smooth", block: "start" }),
        150,
      );
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't calculate. Please retry.");
    } finally {
      setLoading(false);
    }
  };

  const totalForGroup =
    result?.total_per_person?.mid
      ? Number(result.total_per_person.mid) * Number(travelers || 1)
      : 0;

  return (
    <div className="min-h-screen flex flex-col bg-[#FAF7F0]">
      <Seo
        title="Trip budget calculator for Indians — INR estimate by state & style"
        description="Estimate your end-to-end trip cost from India to Southeast Asia by state, days, travelers and travel style — all in INR. Real flight base prices + AI estimates for stays, food, transport, and visas."
        path="/budget"
        jsonLd={[
          webAppLd({
            name: "Flycher Trip Budget Calculator",
            description: "Free Indian trip budget calculator with real flight fares + AI estimates in INR.",
            path: "/budget",
          }),
          breadcrumbLd([
            { name: "Home", path: "/" },
            { name: "Budget Calculator", path: "/budget" },
          ]),
          faqLd([
            { question: "How much does a trip to Thailand cost from India?", answer: "A 7-day trip to Thailand from an Indian metro typically costs ₹50,000–₹85,000 per person for balanced travel (3-4★ hotels, mid-range food). Budget travel: ₹35,000–₹50,000. Luxury: ₹1,20,000+." },
            { question: "How much does a Bali trip cost from India?", answer: "A 7-day Bali trip from India costs around ₹55,000–₹90,000 per person balanced. Flights are ₹20,000–₹35,000 round-trip; villas + scooter + food add the rest." },
            { question: "What's the cheapest international destination from India?", answer: "Nepal (no visa), Thailand (visa-free), Sri Lanka, and Vietnam (cheap e-visa) are the cheapest, often under ₹40,000 per person for a 5-7 day trip including flights." },
            { question: "Does this budget include visa cost?", answer: "Yes. The breakdown includes a visa & miscellaneous line item with the typical INR visa fee for that country (e.g. Vietnam e-visa ~₹2,200, Bali VoA ~₹3,000). For visa-free destinations the line shows ₹0." },
            { question: "Are flight prices live?", answer: "Yes. We pull real round-trip fares from your nearest Indian airport via the TravelPayouts data API (cached daily). The result page shows the real base fare alongside the AI-estimated comfortable range." },
          ]),
        ]}
      />
      <Navbar />
      <main className="flex-1 max-w-5xl mx-auto w-full px-5 sm:px-8 pt-28 pb-20">
        <span className="kicker">— Trip budget calculator</span>
        <h1 className="font-serif text-4xl sm:text-6xl mt-2 text-slate-900 leading-tight">
          Know what your trip will cost.
          <br />
          <span className="italic" style={{ color: "#3DB68B" }}>To the rupee.</span>
        </h1>
        <p className="text-slate-600 mt-4 max-w-2xl">
          Pick where you're starting from, where you're heading, how many days, and how you like to travel. We pull a <strong>real round-trip flight fare</strong> from your nearest international airport and combine it with AI-calibrated estimates for stays, food, activities and the boring stuff (visa, SIM, insurance).
        </p>

        {/* Form */}
        <div data-testid="budget-form" className="mt-8 rounded-2xl bg-white border border-slate-200 shadow-sm p-5 sm:p-6 grid grid-cols-1 md:grid-cols-12 gap-4">
          <label className="md:col-span-4 flex flex-col gap-1.5">
            <span className="text-[10px] uppercase tracking-[0.18em] text-slate-500">From (Indian state)</span>
            <select
              data-testid="budget-state"
              value={state}
              onChange={(e) => setState(e.target.value)}
              className="select-base h-12 text-base"
            >
              {INDIAN_STATES.map((s) => (
                <option key={s}>{s}</option>
              ))}
            </select>
          </label>
          <label className="md:col-span-4 flex flex-col gap-1.5">
            <span className="text-[10px] uppercase tracking-[0.18em] text-slate-500">To (destination)</span>
            <select
              data-testid="budget-country"
              value={slug}
              onChange={(e) => setSlug(e.target.value)}
              className="select-base h-12 text-base"
            >
              {COUNTRIES.map((c) => (
                <option key={c.slug} value={c.slug}>
                  {c.name}
                </option>
              ))}
            </select>
          </label>
          <label className="md:col-span-2 flex flex-col gap-1.5">
            <span className="text-[10px] uppercase tracking-[0.18em] text-slate-500">Days</span>
            <input
              type="number"
              min={1}
              max={30}
              data-testid="budget-days"
              value={days}
              onChange={(e) => setDays(e.target.value)}
              className="input-base h-12 text-base text-center"
            />
          </label>
          <label className="md:col-span-2 flex flex-col gap-1.5">
            <span className="text-[10px] uppercase tracking-[0.18em] text-slate-500">Travellers</span>
            <input
              type="number"
              min={1}
              max={20}
              data-testid="budget-travelers"
              value={travelers}
              onChange={(e) => setTravelers(e.target.value)}
              className="input-base h-12 text-base text-center"
            />
          </label>
          <div className="md:col-span-12">
            <p className="text-[10px] uppercase tracking-[0.18em] text-slate-500 mb-2">Style</p>
            <div className="flex gap-2 -mx-1 px-1 overflow-x-auto flex-nowrap sm:flex-wrap scrollbar-hide">
              {STYLES.map((s) => {
                const active = style === s.key;
                return (
                  <button
                    key={s.key}
                    type="button"
                    data-testid={`budget-style-${s.key}`}
                    onClick={() => setStyle(s.key)}
                    className={`flex-1 sm:flex-none flex flex-col items-start gap-0.5 px-4 sm:px-5 py-3 rounded-2xl whitespace-nowrap border transition ${
                      active ? "text-white shadow-md border-transparent" : "border-slate-200 bg-white hover:border-[#2A7FB8]/40"
                    }`}
                    style={active ? { background: s.color } : {}}
                  >
                    <span className="font-semibold">{s.label}</span>
                    <span className={`text-[10px] ${active ? "text-white/85" : "text-slate-400"}`}>{s.hint}</span>
                  </button>
                );
              })}
            </div>
          </div>
          <div className="md:col-span-12 flex justify-end">
            <button
              type="button"
              onClick={run}
              disabled={loading}
              data-testid="budget-calc-btn"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-full text-sm font-semibold text-white shadow-lg shadow-emerald-200/60 disabled:opacity-50"
              style={{ background: "#3DB68B" }}
            >
              <Calculator size={16} weight="bold" />
              {loading ? "Calculating…" : "Calculate my budget"}
            </button>
          </div>
        </div>

        {/* Result */}
        {loading && (
          <div className="mt-10 rounded-2xl bg-white border border-slate-100 p-8 text-center">
            <div className="w-10 h-10 mx-auto rounded-full border-2 border-[#3DB68B] border-t-transparent animate-spin" />
            <p className="mt-4 text-sm text-slate-600 font-mono uppercase tracking-wider">
              Pulling real flight fares + calibrating per-day costs…
            </p>
          </div>
        )}

        {result && !loading && (
          <section id="budget-result" className="mt-12" data-testid="budget-result">
            {/* Headline number */}
            <div
              className="rounded-2xl p-6 sm:p-8 text-white shadow-xl shadow-emerald-200/40"
              style={{ background: "linear-gradient(135deg, #3DB68B 0%, #2A7FB8 100%)" }}
            >
              <span className="text-[10px] font-mono uppercase tracking-[0.2em] text-white/80">
                Estimated total — {result.travelers} {result.travelers == 1 ? "traveller" : "travellers"} · {result.days} {result.days == 1 ? "day" : "days"} · {STYLES.find((x) => x.key === result.style)?.label}
              </span>
              <div className="flex items-baseline gap-3 mt-2 flex-wrap">
                <CurrencyInr size={36} weight="bold" />
                <span className="font-serif text-4xl sm:text-6xl font-bold">
                  {fmt(totalForGroup).replace("₹", "")}
                </span>
                <span className="text-white/85 text-base">
                  for the whole trip
                </span>
              </div>
              <div className="mt-3 text-sm text-white/95">
                <strong>{fmt(result.total_per_person?.mid)}</strong> per person ({fmt(result.total_per_person?.low)} – {fmt(result.total_per_person?.high)} range)
                {result.real_flight_inr_round_trip ? (
                  <span className="block mt-1 text-xs text-white/80">
                    ✓ Flights are real round-trip fares from {result.origin_iata} cached by Travelpayouts.
                  </span>
                ) : null}
              </div>
            </div>

            {/* Breakdown grid */}
            <div className="mt-6 grid sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="budget-breakdown">
              {BREAKDOWN_META.map((b) => {
                const row = result.breakdown?.[b.key];
                if (!row) return null;
                const { Icon } = b;
                return (
                  <article
                    key={b.key}
                    className="rounded-2xl bg-white border border-slate-200 p-4"
                  >
                    <div className="flex items-center gap-3">
                      <div
                        className="w-9 h-9 rounded-xl grid place-items-center"
                        style={{ background: b.color + "1A", color: b.color }}
                      >
                        <Icon size={18} weight="duotone" />
                      </div>
                      <div>
                        <p className="text-[10px] font-mono uppercase tracking-wider text-slate-400">
                          {b.label}
                        </p>
                        <p className="font-serif text-lg text-slate-900 font-semibold">
                          {fmt(row.mid)}
                        </p>
                      </div>
                    </div>
                    <p className="text-[11px] text-slate-500 mt-2">
                      Range: {fmt(row.low)} – {fmt(row.high)}
                    </p>
                    {row.note && (
                      <p className="text-xs text-slate-600 mt-1 leading-relaxed">{row.note}</p>
                    )}
                  </article>
                );
              })}
            </div>

            {/* Saving tips */}
            {result.saving_tips?.length > 0 && (
              <div className="mt-8 rounded-2xl bg-[#FFF1E8] border border-[#FF7E4D]/15 p-5">
                <span className="kicker">— Smart savings</span>
                <ul className="mt-3 space-y-2">
                  {result.saving_tips.map((t, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm text-slate-800">
                      <Lightning size={14} className="mt-1 flex-shrink-0" color="#FF7E4D" weight="fill" />
                      <span>{t}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* CTAs */}
            <div className="mt-8 flex flex-wrap gap-3">
              <button
                type="button"
                data-testid="budget-build-itinerary"
                onClick={() => {
                  track("budget_to_itinerary_clicked", {
                    country: slug,
                    state,
                    days,
                    travelers,
                    style,
                  });
                  const params = new URLSearchParams({
                    from: state,
                    days: String(days),
                    travelers: String(travelers),
                    style,
                    budget: String(result?.total_per_person?.mid || ""),
                  });
                  navigate(`/itinerary/${slug}?${params.toString()}`);
                }}
                className="inline-flex items-center gap-2 px-5 py-3 rounded-full text-sm font-semibold text-white shadow-md"
                style={{ background: "#FF7E4D" }}
              >
                <Sparkle size={14} weight="fill" />
                Build an itinerary for this budget
                <ArrowRight size={12} weight="bold" />
              </button>
              <button
                type="button"
                data-testid="budget-see-fares"
                onClick={() => navigate(`/explore/${slug}`)}
                className="inline-flex items-center gap-2 px-5 py-3 rounded-full text-sm font-semibold border border-slate-200 bg-white text-slate-700 hover:border-[#2A7FB8] hover:text-[#2A7FB8]"
              >
                <Airplane size={14} weight="duotone" />
                See real flight fares
                <ArrowRight size={12} weight="bold" />
              </button>
            </div>

            <p className="mt-6 text-[11px] text-slate-400 font-mono text-center">
              Estimates are indicative · Final prices on the partner booking page.
            </p>
          </section>
        )}
      </main>
      <Footer />
    </div>
  );
}
